import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { requests } from "../../../../services/http-common";
import { TContractForm } from "../Contracts.model";
import { useEffect, useMemo, useState } from "react";
import { PetsEnum, RecurrenceEnum } from "../../SalesLead/Lead.model";

const toNumberArray = (value: any): number[] => {
  if (Array.isArray(value)) {
    return value.map(Number).filter((item) => !Number.isNaN(item));
  }

  if (typeof value === "string" && value.length) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed)
        ? parsed.map(Number).filter((item) => !Number.isNaN(item))
        : [];
    } catch {
      return [];
    }
  }

  return [];
};

/**
 * endDate is a calendar date, but the API sends it as a UTC instant
 * ("2026-11-10T00:00:00.000Z"). Passing that to new Date() and rendering it in a
 * local-time Calendar shows the previous day west of Greenwich, so read the date
 * part literally instead.
 */
export const parseContractDateOnly = (
  value: string | Date | null | undefined
): Date | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  const [year, month, day] = value.slice(0, 10).split("-").map(Number);

  return new Date(year, month - 1, day);
};

export enum SowFrequencyEnum {
  PER_VISIT = "Per Visit",
  WEEKLY = "Weekly",
  BI_WEEKLY = "Bi-Weekly",
  MONTHLY = "Monthly",
}

export const useContractForm = (
  leadId: string,
  contractId?: string,
  // An uploaded contract carries its own terms on paper, so only the project
  // type (plus the document and signing date, handled in the form) is required.
  isPreSigned = false,
) => {
  const formSchema = useMemo(() => {
    const optional = <T extends Yup.AnySchema>(schema: T, required: T): Yup.AnySchema =>
      isPreSigned ? schema : required;

    return Yup.object().shape({
      contractTypeId: Yup.number().nullable(true).required("Field is required"),
      cancellation: optional(
        Yup.number()
          .transform((value) => (Number.isNaN(value) ? null : value))
          .nullable(true),
        Yup.number()
          .transform((value) => (Number.isNaN(value) ? null : value))
          .required("Field is required")
          .nullable(true),
      ),
      compensation: optional(Yup.string(), Yup.string().required("Field is required")),
      amount: optional(
        Yup.number().nullable(true),
        Yup.number().nullable(true).required("Field is required"),
      ),
      leadId: Yup.string(),
      sowFrequencies: Yup.array(),
      typeOfSpace: optional(Yup.string(), Yup.string().required("Field is required")),
      typeOfFloorSurfaceIds: optional(
        Yup.array().of(Yup.number()),
        Yup.array().min(1, "Field is required").of(Yup.number()).required("Field is required"),
      ),
      suppliesIds: optional(
        Yup.array().of(Yup.number()),
        Yup.array().min(1, "Field is required").of(Yup.number()).required("Field is required"),
      ),
      paperProductIds: optional(Yup.array(), Yup.array().required("Field is required")),
      recurrence: optional(
        Yup.mixed<RecurrenceEnum>().oneOf([...Object.values(RecurrenceEnum), null, undefined]),
        Yup.mixed<RecurrenceEnum>().oneOf(Object.values(RecurrenceEnum)).required("Field is required"),
      ),
    // frequency: Yup.mixed()
    //   .oneOf([...Object.values(JobFrequencyEnum), null])
    //   .when("recurrence", {
    //     is: (recurrence: boolean) => recurrence,
    //     then: Yup.mixed()
    //       .oneOf([...Object.values(JobFrequencyEnum)])
    //       .required("Field is required"),
    //   }),
      pets: optional(
        Yup.mixed<PetsEnum>().oneOf([...Object.values(PetsEnum), null, undefined]),
        Yup.mixed<PetsEnum>().oneOf(Object.values(PetsEnum)).required("Field is required"),
      ),
      scopeOfWorkIds: optional(
        Yup.array().of(Yup.number()),
        Yup.array().min(1, "Field is required").of(Yup.number()).required("Field is required"),
      ),
    });
  }, [isPreSigned]);

  const hookForm = useForm<TContractForm>({
    resolver: yupResolver(formSchema),
    shouldFocusError: false,
  });

  const { reset } = hookForm;
  const [isContractDataLoaded, setIsContractDataLoaded] = useState(false);

  const { refetch: getContarctRefetch } = useQuery({
    queryKey: [`contract-form-${contractId}`],
    queryFn: () => {
      return requests.get(`/contract/${contractId}`);
    },
    enabled: false,
    onSuccess: (data) => {
      reset((values) => ({
        ...values,
        contractTypeId: data.contractTypeId,
        compensation: data.compensation,
        cancellation: data.cancellation,
        amount: data.amount,
        pets: data.pets,
        estimatedSQFT: data.estimatedSQFT,
        typeOfSpace: data.typeOfSpace,
        typeOfFloorSurfaceIds: data.typeOfFloorSurfaces.map(
          (tFS: any) => tFS.id
        ),
        suppliesIds: data.supplies.map((s: any) => s.id),
        paperProductIds: data.paperProducts.map((p: any) => p.id),
        scopeOfWorkIds: data.scopeOfWorks.map((s: any) => s.id),

        sowFrequencies: data.sowFrequencies.map((s:any) => ({
    sowId: s.sowId,
    frequency: s.frequency
  })),

        recurrence: data.recurrence,
        frequency: data.frequency,
        recurEvery: data.recurEvery || 1,
        daysOfWeek: toNumberArray(data.daysOfWeek),
        dayOfMonth: data.dayOfMonth || 1,
        monthOfYear: data.monthOfYear || 0,
        recurrenceEndDate: data.recurrenceEndDate ? new Date(data.recurrenceEndDate) : null,
        endDate: parseContractDateOnly(data.endDate),
        
      }));
      setIsContractDataLoaded(true); 
    },
  });

  const { data: floorSurfacesOptionsData } = useQuery({
    queryKey: ["floor-surfaces-options"],
    queryFn: () => {
      return requests.get("/type-of-floor-surfaces");
    },
  });

  const { data: contractTypesPayload } = useQuery({
    queryKey: ["contract-type-options"],
    queryFn: () => {
      return requests.get(`/contract-type?rows=1000`);
    },
  });

  const { data: suppliesOptionsData } = useQuery({
    queryKey: ["supplies-options"],
    queryFn: () => {
      return requests.get("/supplies");
    },
  });

  const { data: paperProductOptionsData } = useQuery({
    queryKey: ["paper-product-options"],
    queryFn: () => {
      return requests.get("/paper-products");
    },
  });

  const { data: leadPayload } = useQuery({
    queryKey: [`lead-details-${leadId}`],
    queryFn: () => {
      return requests.get(`/lead/${leadId}`);
    },


    onSuccess: (data) => {
      if (!contractId) {
        reset((values) => ({
          ...values,
          contractTypeId: data.contractTypeId,
          estimatedSQFT: data.estimatedSQFT,
        }));
      }

      // TODO: check this later
      // reset((values) => {
      //   const newValues = {
      //     ...values,
      //     leadId: data.id,
      //     sowFrequencies: !data.sowFrequencies?.length
      //       ? data.scopeOfWorks.map((s: any) => ({
      //           sowId: s.id,
      //           frequency: SowFrequencyEnum.PER_VISIT,
      //         }))
      //       : data.sowFrequencies,
      //   };
      //   return newValues;
      // });
    },
  });

  useEffect(() => {
    if (contractId) {
      getContarctRefetch();
    }
  }, [contractId ]);

  const contractTypeOptions = useMemo(() => {
    if (!contractTypesPayload) return [];
    return contractTypesPayload.result.map((contractType: any) => ({
      label: contractType.name,
      value: contractType.id,
      hasSowTemplate: contractType.hasSowTemplate,
    }));
  }, [contractTypesPayload]);

  const floorSurfaceOptions = useMemo(() => {
    if (!floorSurfacesOptionsData) return [];

    return floorSurfacesOptionsData.result.map((option: any) => ({
      label: option.name,
      value: option.id,
    }));
  }, [floorSurfacesOptionsData]);

  const suppliesOptions = useMemo(() => {
    if (!suppliesOptionsData) return [];

    return suppliesOptionsData.result.map((option: any) => ({
      label: option.name,
      value: option.id,
    }));
  }, [suppliesOptionsData]);

  const paperProductOptions = useMemo(() => {
    if (!paperProductOptionsData) return [];

    return paperProductOptionsData.result.map((option: any) => ({
      label: option.name,
      value: option.id,
    }));
  }, [paperProductOptionsData]);

  return {
    hookForm,
    leadPayload,
    contractTypeOptions,
    floorSurfaceOptions,
    suppliesOptions,
    paperProductOptions,
    isContractDataLoaded
  };
};
